import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { FullTable as Table, MobileTable } from '../../../components';
import { useToastContext } from '../../../hooks';
import { SMPAPI } from '../../../fetch/smp';
import { ILicense } from '../../../models/smp/license';
import ApplicationLicenses from './ApplicationLicenses';

interface RouteParams {
  applicationId?: string;
}

const Licenses = () => {
  const { applicationId } = useParams<RouteParams>();
  const history = useHistory();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const { showToast } = useToastContext();

  const [licenses, setLicenses] = useState<ILicense[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setRowsPerPage] = useState(10);
  const [recordCount, setRecordCount] = useState(0);

  const fetchGlobalLicenses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await SMPAPI.fetchAllLicenses({ page: page + 1, perPage });
      setLicenses(data.records);
      setRecordCount(data.totalRecordCount);
    } catch (error: any) {
      showToast('error', error?.detail || 'Error fetching licenses');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, showToast]);

  useEffect(() => {
    if (!applicationId) {
      fetchGlobalLicenses();
    }
  }, [fetchGlobalLicenses, applicationId]);

  const columns = useMemo(() => [
    { accessor: 'applicationName', Header: 'Application Name' },
    { accessor: 'licenseName', Header: 'License Name' },
    { accessor: 'licenseType', Header: 'Type' },
    { accessor: 'licenseStatus', Header: 'Status' },
    { accessor: 'startDate', Header: 'Start Date' },
    { accessor: 'endDate', Header: 'End Date' },
    { accessor: 'totalContractValue', Header: 'Total Value' },
    {
      accessor: 'actions',
      Header: '',
      Cell: ({ row: { original } }: any) => (
        <>
          <IconButton onClick={() => history.push(`/smp/licenses/${original.applicationID}`)}>
            <OpenInNew color="primary" />
          </IconButton>
        </>
      ),
    },
  ], [history]);

  if (applicationId) {
    return <ApplicationLicenses />;
  }

  return (
    <Box sx={{ backgroundColor: '#f0f0f0', p: 2, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        Global Licenses
      </Typography>

      <Table
        columns={columns}
        data={licenses}
        isLoading={loading}
        stickyHeader
        serverPage={page}
        serverRecordCount={recordCount}
        serverPerPage={perPage}
        handlePage={setPage}
        handleRowsPerPage={setRowsPerPage}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
        mobileProps={{
          fields: columns.map(c => ({
            name: c.Header,
            accessor: c.accessor,
            Cell: c.Cell,
          })),
          mobileAccordionLabel: (original: any) => original.applicationName,
        }}
      />
    </Box>
  );
};

export default Licenses;
